import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard, Users, FolderOpen, HeartPulse,
  Stethoscope, FileText, Sparkles, BarChart3, LogOut, User, Activity, Shield,
  Menu, X, ChevronRight, FlaskConical, Brain, Layers,
  ScrollText, BookOpen, Calendar, Leaf, Zap, Database, Play, Building2, Filter, Workflow, ClipboardCheck, Beaker, GitBranch, Gauge, HandHeart, Compass, Map, MapPin, Upload, TrendingUp
} from 'lucide-react';

const SYMBOL_LOGO = "https://customer-assets.emergentagent.com/job_neuro-care-hub-2/artifacts/zntnld86_Lifely%20Symbol%20AI.png";
const TEXT_LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_neuro-care-hub-2/artifacts/yxqxnzye_ReGenflo%20logo%202%20contrast.png";

// ── Grouped Navigation ──────────────────────────────────────────
const NAV_GROUPS = [
  {
    label: 'Operations',
    items: [
      { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/admin/crm', label: 'CRM Pipeline', icon: Users, roles: ['admin', 'counselor'] },
      { path: '/admin/users', label: 'Users & Roles', icon: Users, roles: ['admin'] },
      { path: '/admin/care-ops', label: 'Care Operations', icon: HeartPulse, roles: ['admin', 'counselor', 'health_coach'] },
      { path: '/admin/tracking-os', label: 'Tracking OS', icon: ClipboardCheck, roles: ['admin', 'counselor', 'health_coach', 'doctor'] },
    ],
  },
  {
    label: 'Clinical',
    items: [
      { path: '/admin/biocontext-memory', label: 'Biological Context Memory — Canonical', icon: Brain, roles: ['admin'] },
      { path: '/admin/counselor-followup', label: 'Counselor Follow-Up', icon: ScrollText, roles: ['admin', 'counselor', 'health_coach', 'doctor'] },
      { path: '/admin/journey', label: 'Journey Model', icon: Layers, roles: ['admin', 'counselor', 'doctor'] },
      { path: '/admin/clinical', label: 'Clinical Review', icon: Stethoscope, roles: ['admin', 'doctor'] },
      { path: '/admin/mindflo-review', label: 'MindFlo Review', icon: Brain, roles: ['admin', 'counselor', 'health_coach', 'doctor'] },
      { path: '/admin/lab-intelligence', label: 'Lab Intelligence', icon: FlaskConical, roles: ['admin', 'counselor', 'health_coach', 'doctor'] },
      { path: '/admin/timeline-review', label: 'Timeline Review', icon: BarChart3, roles: ['admin', 'counselor', 'health_coach', 'doctor'] },
      { path: '/admin/engagement', label: 'Care Intelligence', icon: Activity, roles: ['admin', 'counselor', 'health_coach', 'doctor'] },
      { path: '/admin/support-os', label: 'Care Support', icon: HeartPulse, roles: ['admin', 'counselor', 'health_coach', 'doctor'] },
      { path: '/admin/autonomy-monitoring', label: 'Autonomy Monitor', icon: Shield, roles: ['admin', 'counselor', 'health_coach', 'doctor'] },
    ],
  },
  {
    label: 'Building',
    items: [
      { path: '/admin/decode-wizard', label: 'DECODE Workspace', icon: Brain, roles: ['admin'] },
      { path: '/admin/protocol-flow', label: 'Protocol Flow', icon: Layers, roles: ['admin'] },
      { path: '/admin/protocol-wizard', label: 'Protocol Wizard', icon: Layers, roles: ['admin'] },
      { path: '/admin/executable-protocol', label: 'Executable Protocol', icon: Layers, roles: ['admin', 'counselor', 'health_coach', 'doctor'] },
      { path: '/admin/schedule-editor', label: 'Schedule Editor', icon: Calendar, roles: ['admin'] },
      { path: '/admin/lifeflo-definitions', label: 'LifeFlō Definitions', icon: Leaf, roles: ['admin'] },
      { path: '/admin/medication-definitions', label: 'Medications', icon: HeartPulse, roles: ['admin'] },
    ],
  },
  {
    label: 'Formulation Management',
    items: [
      { path: '/admin/formulation/dashboard', label: 'Formulation Dashboard', icon: LayoutDashboard, roles: ['admin', 'counselor', 'health_coach', 'doctor'], testId: 'formulation-dashboard-link' },
      { path: '/admin/formulation/raw-bases', label: 'Raw Base Library', icon: Beaker, roles: ['admin'], testId: 'raw-base-library-link' },
      { path: '/admin/formulation/verification', label: 'Verification Queue', icon: ClipboardCheck, roles: ['admin', 'counselor'], testId: 'verification-queue-link' },
    ],
  },
  {
    label: 'NeuroFlow™',
    items: [
      { path: '/admin/neuroflow', label: 'NeuroFlow Overview', icon: Brain, roles: ['admin', 'counselor', 'health_coach', 'doctor'], testId: 'neuroflow-overview-link' },
    ],
  },
  {
    label: 'Wearables',
    items: [
      { path: '/admin/wearable-integrations', label: 'Wearable Integrations', icon: Activity, roles: ['admin', 'counselor', 'health_coach', 'doctor'], testId: 'wearable-integrations-link' },
    ],
  },
  {
    label: 'Terra Growth OS™',
    items: [
      { path: '/admin/growth-command-center', label: 'Growth Command Center', icon: Sparkles, roles: ['admin', 'terra_head'], testId: 'growth-command-center-link' },
      { path: '/centre/growth-dashboard',     label: 'Centre Growth Dashboard', icon: LayoutDashboard, roles: ['admin', 'terra_head'], testId: 'centre-growth-dashboard-link' },
      { path: '/centre/events',               label: 'Growth Events',          icon: Calendar, roles: ['admin', 'terra_head', 'counselor', 'health_coach', 'representative'], testId: 'centre-events-link' },
      { path: '/counselor/pipeline',          label: 'Pipeline · depth-first', icon: Users,    roles: ['admin', 'counselor', 'health_coach', 'terra_head', 'representative'], testId: 'counselor-pipeline-link' },
      { path: '/counselor/perception-tracker', label: 'Perception Tracker',   icon: Brain,    roles: ['admin', 'counselor', 'health_coach', 'terra_head', 'representative'], testId: 'perception-tracker-link' },
      { path: '/me/execution-sheet',          label: 'Daily Execution Sheet',  icon: ClipboardCheck, roles: ['admin', 'counselor', 'health_coach', 'terra_head', 'representative'], testId: 'daily-execution-sheet-link' },
    ],
  },
  {
    label: 'Terra Growth OS™ · Intelligence',
    items: [
      { path: '/intelligence/friction-matrix',       label: 'Friction Matrix',         icon: Workflow,    roles: ['admin', 'terra_head', 'counselor', 'health_coach'], testId: 'friction-matrix-link' },
      { path: '/intelligence/territory-maturity',    label: 'Territory Maturity',      icon: Building2,   roles: ['admin', 'terra_head'],                              testId: 'territory-maturity-link' },
      { path: '/intelligence/event-type-efficiency', label: 'Event-Type Efficiency',   icon: Calendar,    roles: ['admin', 'terra_head', 'counselor', 'health_coach'], testId: 'event-type-efficiency-link' },
      { path: '/intelligence/ecosystem-resistance',  label: 'Ecosystem Resistance',    icon: Filter,      roles: ['admin', 'terra_head', 'counselor', 'health_coach'], testId: 'ecosystem-resistance-link' },
      { path: '/intelligence/perception-leakage',    label: 'Perception Leakage',      icon: Activity,    roles: ['admin', 'terra_head', 'counselor', 'health_coach'], testId: 'perception-leakage-link' },
      { path: '/intelligence/counselor-capability',  label: 'Counselor Capability',    icon: Sparkles,    roles: ['admin', 'terra_head', 'counselor', 'health_coach'], testId: 'counselor-capability-link' },
      { path: '/intelligence/referral-graph',        label: 'Referral Graph',          icon: GitBranch,   roles: ['admin', 'terra_head', 'counselor', 'health_coach'], testId: 'referral-graph-link' },
      { path: '/intelligence/rss-dashboard',         label: 'RSS Intelligence',        icon: Gauge,       roles: ['admin', 'terra_head'],                              testId: 'rss-intelligence-link' },
      { path: '/intelligence/movement-digest',       label: 'Weekly Movement Digest',  icon: ScrollText,  roles: ['admin', 'terra_head'],                              testId: 'movement-digest-link' },
      { path: '/intelligence/territory-heatmaps',    label: 'Territory Heatmaps',      icon: Map,         roles: ['admin', 'terra_head'],                              testId: 'territory-heatmaps-link' },
      { path: '/intelligence/territory-map',         label: 'Territory Map',           icon: MapPin,      roles: ['admin', 'terra_head'],                              testId: 'territory-map-link' },
      { path: '/intelligence/territory-evolution',   label: 'Territory Evolution',     icon: TrendingUp,  roles: ['admin', 'terra_head'],                              testId: 'territory-evolution-link' },
      { path: '/admin/growth-import',                label: 'Growth Import',           icon: Upload,      roles: ['admin'],                                            testId: 'growth-import-link' },
    ],
  },
  {
    label: 'Terra Growth OS™ · Sustainability',
    items: [
      { path: '/centre/war-room',                    label: 'Weekly War Room',         icon: Compass,     roles: ['admin', 'terra_head'],                              testId: 'weekly-war-room-link' },
      { path: '/centre/stewardship-letter',          label: 'Stewardship Letter',      icon: FileText,    roles: ['admin', 'terra_head'],                              testId: 'stewardship-letter-link' },
      { path: '/sustainability/me',                  label: 'My Reflection',           icon: Leaf,        roles: ['admin', 'terra_head', 'counselor', 'health_coach'], testId: 'my-sustainability-link' },
      { path: '/sustainability/team',                label: 'Recovery & Sustainability', icon: HandHeart, roles: ['admin', 'terra_head'],                              testId: 'counselor-sustainability-link' },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { path: '/admin/execution-intelligence', label: 'Execution Intelligence', icon: Brain, roles: ['admin', 'counselor', 'health_coach', 'doctor'] },
      { path: '/admin/correlation-insights', label: 'Correlation Insights', icon: Zap, roles: ['admin', 'counselor', 'health_coach', 'doctor'] },
      { path: '/admin/pattern-narrative', label: 'Pattern Narrative', icon: Sparkles, roles: ['admin', 'counselor', 'health_coach', 'doctor'] },
    ],
  },
  {
    label: 'Content',
    items: [
      { path: '/admin/content-publishing', label: 'Content & Insights', icon: BookOpen, roles: ['admin', 'counselor', 'health_coach'] },
      { path: '/admin/documents', label: 'Documents', icon: FolderOpen, roles: ['admin', 'counselor', 'health_coach'] },
      { path: '/admin/report-generator', label: '20-Day Report', icon: FileText, roles: ['admin'] },
      { path: '/admin/reports', label: 'Report Management', icon: FileText, roles: ['admin'] },
    ],
  },
  {
    label: 'System',
    items: [
      { path: '/admin/data-management', label: 'Data Management', icon: Database, roles: ['admin'] },
      { path: '/admin/analytics', label: 'Analytics', icon: BarChart3, roles: ['admin'] },
      { path: '/admin/audit', label: 'Audit Log', icon: ScrollText, roles: ['admin'] },
      { path: '/admin/governance', label: 'Governance Drift', icon: Shield, roles: ['admin', 'counselor'] },
      { path: '/admin/support-visibility', label: 'Support Visibility', icon: Filter, roles: ['admin', 'counselor', 'health_coach', 'doctor'] },
      { path: '/admin/protocol-evolutions', label: 'Protocol Evolutions', icon: Workflow, roles: ['admin', 'counselor', 'health_coach', 'doctor'] },
      { path: '/admin/org-structure', label: 'Org Structure', icon: Building2, roles: ['admin'] },
      { path: '/admin/deployment', label: 'Deployment', icon: Shield, roles: ['admin'] },
      { path: '/centre/dashboard', label: 'Ops Cockpit', icon: Activity, roles: ['admin', 'counselor', 'health_coach', 'doctor', 'flow_steward', 'terra_head'] },
      { path: '/admin/care-command-center', label: 'Command Center', icon: Shield, roles: ['admin'] },
      { path: '/admin/care-flow', label: 'Care Flow', icon: Users, roles: ['admin', 'counselor', 'health_coach', 'doctor'] },
      { path: '/admin/execution-flow', label: 'Execution Flow', icon: Activity, roles: ['admin', 'health_coach'] },
    ],
  },
  {
    label: 'Internal Tools',
    items: [
      { path: '/admin/ai-workbench', label: 'AI Workbench', icon: Sparkles, roles: ['admin'] },
      { path: '/admin/demo-simulation', label: 'Demo Simulation', icon: Play, roles: ['admin'] },
      { path: '/admin/demo-control', label: 'Demo Engine', icon: Play, roles: ['admin'] },
    ],
  },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filteredGroups = NAV_GROUPS.map(group => ({
    ...group,
    items: group.items.filter(item => !item.roles || item.roles.includes(user?.role)),
  })).filter(group => group.items.length > 0);

  const renderNavItems = (items, showLabel = true) => (
    items.map((item) => {
      const isActive = location.pathname === item.path;
      return (
        <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}>
          <div
            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${isActive
              ? 'bg-adm-accent/12 text-adm-accent'
              : 'text-adm-secondary hover:text-adm-primary hover:bg-white/[0.03]'
            }`}
            data-testid={item.testId || `nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
          >
            <item.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
            {showLabel && <span className="text-[13px] font-['Work_Sans']">{item.label}</span>}
          </div>
        </Link>
      );
    })
  );

  return (
    <div className="min-h-screen bg-adm-bg admin-workspace" data-testid="admin-layout">
      {/* ── Top Navigation ──────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-adm-bg/95 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="hidden lg:flex text-adm-secondary hover:text-adm-primary hover:bg-white/5"
                data-testid="sidebar-toggle"
              >
                <Menu className="w-4.5 h-4.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden text-adm-secondary hover:text-adm-primary hover:bg-white/5"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-4.5 h-4.5" /> : <Menu className="w-4.5 h-4.5" />}
              </Button>
              <Link to="/admin" className="flex items-center gap-2.5">
                <img src={SYMBOL_LOGO} alt="Lifely" className="h-8 w-auto" />
                <div className="hidden sm:flex items-center gap-2">
                  <img src={TEXT_LOGO_LIGHT} alt="ReGenflō" className="h-6 w-auto" />
                  <span className="text-[10px] uppercase tracking-[0.15em] text-adm-tertiary font-['Work_Sans'] border-l border-white/10 pl-2">Admin</span>
                </div>
              </Link>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2.5 text-adm-secondary hover:text-adm-primary hover:bg-white/5" data-testid="user-menu">
                  <div className="w-7 h-7 rounded-full bg-adm-accent/15 flex items-center justify-center">
                    <User className="w-3.5 h-3.5 text-adm-accent" />
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-[13px] font-medium text-adm-primary font-['Work_Sans']">{user?.name}</p>
                    <p className="text-[10px] text-adm-tertiary capitalize font-['Work_Sans']">{user?.role}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-adm-panel border-white/10">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-adm-primary font-['Work_Sans']">{user?.name}</p>
                  <p className="text-xs text-adm-tertiary font-['Work_Sans']">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  className="text-adm-secondary focus:text-adm-primary focus:bg-white/5 font-['Work_Sans']"
                  data-testid="logout-btn"
                  onClick={handleLogout}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* ── Sidebar — Desktop ──────────────────────── */}
      <aside className={`fixed left-0 top-14 bottom-0 z-40 bg-adm-surface/80 backdrop-blur-sm border-r border-white/[0.06] transition-all duration-300 hidden lg:block overflow-y-auto ${sidebarOpen ? 'w-60' : 'w-14'}`}>
        <div className="py-3 px-2">
          {sidebarOpen ? (
            filteredGroups.map((group) => (
              <div key={group.label} className="mb-1">
                <p className="text-[9px] uppercase tracking-[0.18em] text-adm-tertiary px-3 pt-4 pb-1.5 font-['Work_Sans'] font-medium">
                  {group.label}
                </p>
                <div className="space-y-0.5">
                  {renderNavItems(group.items)}
                </div>
              </div>
            ))
          ) : (
            filteredGroups.flatMap(g => g.items).map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <div className={`flex items-center justify-center py-2.5 rounded-lg transition-all mb-0.5 ${isActive ? 'bg-adm-accent/12 text-adm-accent' : 'text-adm-secondary hover:text-adm-primary hover:bg-white/[0.03]'}`} title={item.label}>
                    <item.icon className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </aside>

      {/* ── Sidebar — Mobile ───────────────────────── */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
          <aside className="absolute left-0 top-14 bottom-0 w-64 bg-adm-surface border-r border-white/[0.06] overflow-y-auto">
            <div className="py-3 px-2">
              {filteredGroups.map((group) => (
                <div key={group.label} className="mb-1">
                  <p className="text-[9px] uppercase tracking-[0.18em] text-adm-tertiary px-3 pt-4 pb-1.5 font-['Work_Sans'] font-medium">
                    {group.label}
                  </p>
                  <div className="space-y-0.5">
                    {renderNavItems(group.items)}
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      )}

      {/* ── Main Content ───────────────────────────── */}
      <main className={`pt-14 min-h-screen transition-all duration-300 ${sidebarOpen ? 'lg:ml-60' : 'lg:ml-14'}`}>
        <div className="p-5 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
